import React, {useMemo} from 'react'
import get from 'lodash/get'

import './grid.scss'
import ButtonBase from '../ButtonBase'
import {FilterButton} from './FilterPanel'
import PaginationPanel from './PaginationPanel'
import PropTypes from 'prop-types'
import TooltipInfo from '../TooltipInfo'

const renderCellValue = (column, data) => {
  const value = get(data, column.accessor, '')
  if (column.renderCell) {
    return column.renderCell(value, data)
  } else {
    return <span className={'Suc-ellipsis'} title={typeof value === "string" ? value : ""}>{value}</span>
  }
}

const Row = ({columns, data}) => {
  const {className = '', skeleton = false, ...cellProps} = data.cellProps || {}
  return <div className={`Suc-row ${data.disableSelection ? 'Suc-disabled-row' : ''}`}>
    {columns.map((column, index) => {
      const cellValue = renderCellValue(column, data)
      const {className: columnClassName = ''} = column
      return <span
        className={`Suc-cell Suc-col-${index} ${index === columns.length - 1 ? 'Suc-last-col' : ''} ${className} ${columnClassName} ${column?.sticky ? 'Suc-sticky' : ''}`}
        key={column.id} {...cellProps}>{skeleton ? <div className={'Suc-skeleton'}/> : cellValue}</span>
    })}</div>
}

const HeaderCell = ({column, isExternalFilter, index, isLast}) => {
  const {sortable, Header, toggleSort, sortState, filterState, filterOptions, info, infoProps = {}, id, className = '', sticky = false} = column
  return <div
    className={`Suc-header-cell Suc-col-${index} ${isLast ? 'Suc-last-col' : ''} ${className} ${sticky ? 'Suc-sticky' : ''}`}
    key={id}>
    <div className={'Suc-left-border'}/>
    {info &&
    <TooltipInfo appendToBody={true} className={'Suc-table-header-tooltip'} {...infoProps}>{info}</TooltipInfo>}
    <div className={'Suc-header-name'}>{Header} {filterState?.activeCount ? `(${filterState.activeCount})` : ""}</div>
    {filterOptions && !isExternalFilter && <FilterButton column={column}/>}
    {sortable && <SortButton handleSort={toggleSort} sortState={sortState}/>}
  </div>
}

/** Data table - Should be used with useTable hook, all props go for useTable which return the gridProps (apart from className, disableAutoSizeColumns and isExternalFilter) */
const Grid = React.forwardRef(({organizedRows, columns, pagination, className, pageSize, disableAutoSizeColumns, isExternalFilter, horizontalScroll}, ref) => {
  const gridTemplateColumns = useMemo(() => columns.reduce((acc, {width}) => acc + (width ? `${width} ` : disableAutoSizeColumns || horizontalScroll ? '1fr ' : 'auto '), ''), [columns, disableAutoSizeColumns])
  return (
    <div className={'Suc-grid-wrapper'}>
      <div className={`Suc-grid ${className}`}
           style={{gridTemplateColumns}}
           data-columns-count={columns.length}>
        <div className="Suc-header-row">
          {columns.map((column, index) => <HeaderCell key={column.id} column={column}
                                                      isExternalFilter={isExternalFilter} index={index}
                                                      isLast={(columns.length - 1) === index}/>)}
        </div>
        <div className='Suc-body'>
          {pagination?.pageRows?.map((dataRow) => <Row key={dataRow.id} columns={columns} data={dataRow}/>)}
        </div>
      </div>
      {pagination?.pageCount > 1 &&
      <PaginationPanel pagination={pagination} columnCount={columns.length + 1} pageSize={pageSize}
                       totalRows={organizedRows.length}/>}
    </div>
  )
})

const filterOptionShape = {
  /** value the data should be filtered for */
  value: PropTypes.string,
  /** label of filter */
  label: PropTypes.string
}

const columnShape = {
  /** column header label text/ react node */
  Header: PropTypes.node.isRequired,
  /** key of relevant property from data */
  accessor: PropTypes.string.isRequired,
  /** Is column should be sortable */
  sortable: PropTypes.bool,
  /** Is filterable? pass filter options or allow auto-creation of the options by passing 'auto'.  */
  filterOptions: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape(filterOptionShape)), PropTypes.oneOf(['auto'])]),
  /** informative tooltip on column header */
  info: PropTypes.node,
  /** column width - valid grid template column value. required on horizontal scroll */
  width: PropTypes.string,
  /** custom cell content (cellData,rowData)=>cellContent */
  renderCell: PropTypes.func,
  /** custom filter-panel label content (val)=>labelContent */
  renderFilterPanelLabel: PropTypes.func,
  /** custom classname for the column. header and cells */
  className: PropTypes.string,
  /** InfoTooltip props */
  infoProps: PropTypes.object,
  /** On horizontal scroll, keep column sticky, can be apply to first and last columns */
  sticky: PropTypes.bool
}

const rowShape = {
  /** values here can be string, property names according to columns 'accessor' values */
  /** props for the rows cells in this row (can have className) or have skeleton:true  */
  cellProps: PropTypes.object,
  /** if row selection is active, you can disable selecting the row with this property  */
  disableSelection: PropTypes.bool
}

Grid.propTypes = {
  /** grid columns */
  columns: PropTypes.arrayOf(PropTypes.shape(columnShape)),
  /** table data  */
  data: PropTypes.arrayOf(PropTypes.shape(rowShape)),
  /** Number of rows in page */
  pageSize: PropTypes.number,
  /** custom classname */
  className: PropTypes.string,
  /** Are rows selectable? */
  selectionType: PropTypes.oneOf(['NONE', 'SINGULAR', 'MULTIPLE']),
  /** regular sorting mechanism */
  isSorting: PropTypes.bool,
  /** disable columns automatic width setting according to data */
  disableAutoSizeColumns: PropTypes.bool,
  /** should be supplied if 'isSorting' is false - Not supported yet */
  externalSort: PropTypes.bool,
  /** External filters*/
  externalFilter: PropTypes.object,
  /** initial sort state - usually passed from app state */
  initialSortState: PropTypes.object,
  /** initial filter state - usually passed from app state */
  initialFilterState: PropTypes.object,
  /** Array of row Ids, that should be selected by default. */
  defaultSelectedRows: PropTypes.arrayOf(PropTypes.string),
  /** Don't show filters in the header cells - you can add the ExternalTableFilters component */
  isExternalFilter: PropTypes.bool,
  /** Allow horizontal scroll, each columns need width definition*/
  horizontalScroll: PropTypes.bool
}

Grid.defaultProps = {
  pageSize: 50,
  disableAutoSizeColumns: false,
  className: ''
}

export default Grid

const SortButton = ({handleSort, sortState = null}) => {
  return <ButtonBase type="button"
                     className={`Suc-sort-button ${sortState === null ? 'Suc-none' : ''} ${sortState === 'asc' ? 'Suc-asc' : ''} ${sortState === 'desc' ? 'Suc-dsc' : ''}`}
                     onClick={handleSort}/>
}
